import { CreateSnapQuoteLineOutput } from '@snap-types/actions/create-snap-quote-line';
import { SnapSuggestionStatus } from '@snap-types/models/common';
import { DocumentWithUser } from '@snap-types/models/document';
import { OriginalSource, PriceStrategy } from '@snap-types/models/enums';
import type { Inventory } from '@snap-types/models/inventory';
import { Decimal } from '@snap-types/types/decimal';
import { Nullish } from '@snap-types/types/nullish';

import { ProductOptionType } from './product-option';
import { QuotePriceType } from './quote-price';
import { QuoteBuilderWorkOrderType } from './work-order';

export type QuoteLineLastWinType = {
  hasLastWin: boolean;
  orderId: string | null;
  orderDate: Nullish<Date | null>;
  wonAt: Nullish<Date | null>;
  quantity: Decimal | null;
  unitCost: Decimal | null;
  unitLaborCost: Decimal | null;
  unitMaterialCost: Decimal | null;
  unitServiceCost: Decimal | null;
  unitVariableCosts: Decimal | null;
  unitPrice: Decimal | null;
  grossMargin: Decimal | null;
  contributionMargin: Decimal | null;
  leadTime: Nullish<number | null>;
};

type QuoteLineStaticType = {
  uuid: string;
  description: Nullish<string>;
  lineNumber: number;
  partId: string;
  partOriginalSource: OriginalSource;
  type: string;
  material: Nullish<string>;
  estQtyOnHand: Nullish<number>;
  lastWin: QuoteLineLastWinType;
  inventory: Nullish<Inventory>;
  priceStrategy: PriceStrategy;
  discountPrices: Nullish<CreateSnapQuoteLineOutput['discountPrices']>;
  custPriceEffects: Nullish<CreateSnapQuoteLineOutput['custPriceEffects']>;
  marketPrices: Nullish<CreateSnapQuoteLineOutput['marketPrices']>;
  configuratorSnapshot: Nullish<
    CreateSnapQuoteLineOutput['configuratorSnapshot']
  >;
  partSpecs?: Nullish<string>;
  priceAdjustment: Decimal | null;
  priceAdjustmentAmount: Decimal | null;
  priceSegments: CreateSnapQuoteLineOutput['priceSegments'];
  craftProductOptions: CreateSnapQuoteLineOutput['craftProductOptions'];

  lastWinOrderLineId: string | null;

  automatable: boolean;
  automatedLeadTime: number | null;
  leadTimeSuggestionStatus: SnapSuggestionStatus;
  suggestedPricesStatus: SnapSuggestionStatus;
};

export enum QuoteInfoTab {
  CostOverview = 'CostOverview',
  QuoteHistory = 'QuoteHistory',
  CostEstimator = 'CostEstimator',
  OrderHistory = 'OrderHistory',
  DiscountPriceBook = 'DiscountPriceBook',
  MarketPriceBook = 'MarketPriceBook',
  CostBreaks = 'CostBreaks',
  PriceSegments = 'PriceSegments',
  CAPPriceSegments = 'CAPPriceSegments',
  MetricsDevView = 'MetricsDevView',
  CostsDebug = 'CostsDebug',
}

type QuoteLineInternalType = {
  isInfoVisible: boolean;
  infoTab: QuoteInfoTab;
  status: 'ready' | 'deleting' | 'deletion-failed' | 'syncing';
  productOptionsChangeUnitPrice: boolean;
};

type QuoteLineDynamicType = {
  userLeadTime: Nullish<number>;
  finishedPartId: string;
};

type QuoteLineEditableType = {
  customerPartId: Nullish<string>;
  /** Lead time is stored in days but is typically displayed in weeks */
  leadTime: Nullish<number>;
  leadTimeNotes: Nullish<string>;
  specs: Nullish<string>;
};

export type QuoteLineType = QuoteLineStaticType &
  QuoteLineInternalType &
  QuoteLineDynamicType &
  QuoteLineEditableType & {
    quotePriceUuids: string[];
    quotePrices: Record<string, QuotePriceType>;
    snapWorkOrder: QuoteBuilderWorkOrderType | null;
    documentIds: string[];
    documents: Record<string, DocumentWithUser>;
    productOptions: Record<string, ProductOptionType>;
    productOptionsUuids: string[];
  };
